import { CancelTokenSource } from 'axios';
import { BillXmlStatus } from 'ts-types/api.enums';
import {
  BillXmlRequestDto,
  MediDataNotificationsDto,
  SendToMediDataResponseDto,
  StornoRejectedBillsResponseDto,
  XmlResponseRequestDto
} from 'ts-types/api.types';
import axios from './http';

const {REACT_APP_API_BASE_URL: baseUrl} = process.env;
const apiUri = `${baseUrl}/api/bills/xml`;
const apiBillRequestUri = `${apiUri}/request`;
const apiBillResponseUri = `${apiUri}/response`;

export const getBillXmlRequests = (
    tariffId: number,
    startDate: String,
    endDate: String,
    filterByStatus: Array<BillXmlStatus>,
    mandateId: number,
    source: CancelTokenSource)
    : Promise<Array<BillXmlRequestDto>> => {

  return axios
      .get(`${apiBillRequestUri}`,
          {
            params: {tariffId, startDate, endDate, filterByStatus, mandateId},
            cancelToken: source.token
          })
      .then(response => response.data);
};

export const updateRemoteStatus = (
    mandateId: number, source: CancelTokenSource)
    : Promise<Array<BillXmlRequestDto>> => {

  return axios
      .post(`${apiBillRequestUri}/update-remote-status/${mandateId}`, {}, {cancelToken: source.token})
      .then(response => response.data);
};

export const getAutomaticBillXmlRequests = (
    automaticBillRunId: number, source: CancelTokenSource)
    : Promise<Array<BillXmlRequestDto>> => {

  return axios
      .get(`${apiBillRequestUri}/automatic-bill/${automaticBillRunId}`,
          {cancelToken: source.token})
      .then(response => response.data);
};

export const updateBillXmlRequest = (
    billXmlRequestId: number, source: CancelTokenSource)
    : Promise<Array<BillXmlRequestDto>> => {

  var params = new URLSearchParams();
  params.append('billXmlRequestId', "" + billXmlRequestId);

  return axios
      .post(`${apiBillRequestUri}/update`, params, {cancelToken: source.token})
      .then(response => response.data);
};

export const downloadXml = (
    billXmlRequestId: number, source: CancelTokenSource)
    : Promise<String> => {

  return axios
      .get(`${apiBillRequestUri}/${billXmlRequestId}/xml`, {cancelToken: source.token})
      .then((response) => {
        // const type = response.headers['content-type'];
        const type = "application/xml";
        // @ts-ignore
        const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([BOM, response.data], {type: type});
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = response.headers['content-disposition'];
        const filenameRegex = /filename=(.*)/;
        const regexResponse = filenameRegex.exec(link.download);
        const defaultFileName = `bill-xml-request-${billXmlRequestId}.xml`;
        const fileName = regexResponse ?
            regexResponse[1] ? regexResponse[1] : defaultFileName :
            defaultFileName;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        return "";
      });
};

export const sendToMediData = (
    mandateId: number,
    billXmlRequestIds: Array<number>,
    source: CancelTokenSource)
    : Promise<SendToMediDataResponseDto> => {

  const data = {
    mandateId,
    billXmlRequestIds
  }

  return axios
      .post(`${apiBillRequestUri}/sendToMediData`, data, {cancelToken: source.token})
      .then(response => response.data);
};

export const recreateXmlBillsWithVersion44 = (
    mandateId: number,
    source: CancelTokenSource): Promise<string> => {

  return axios
      .post(`${apiBillRequestUri}/recreateXmlBillsWithVersion44`,
          null, {params: {mandateId}, cancelToken: source.token})
      .then(response => response.data);
};

export const getBillXmlResponse = (
    mandateId: number,
    tariffId: number,
    billId: number | undefined,
    patientId: number | undefined,
    source: CancelTokenSource): Promise<Array<XmlResponseRequestDto>> => {

  const params: any = {mandateId, tariffId};
  if (billId) {
    params.billId = billId;
  }
  if (patientId) {
    params.patientId = patientId;
  }

  return axios
      .get(`${apiBillResponseUri}`,
          {params: params, cancelToken: source.token})
      .then(response => response.data);
};

export const getNumberOfXmlResponses = (mandateId: number, source: CancelTokenSource) => {

  return axios
      .get(`${apiUri}/numberOfXmlResponses`,
          {params: {mandateId}, cancelToken: source.token})
      .then(response => response.data);
};

export const stornoRejectedBills = (
    mandateId: number,
    tariffId: number,
    billXmlRequestIds: Array<String>,
    preview: boolean,
    archiveOnly: boolean,
    source: CancelTokenSource)
    : Promise<StornoRejectedBillsResponseDto> => {

  const data = {
    mandateId,
    tariffId,
    billXmlRequestIds,
    preview,
    archiveOnly
  }

  return axios
      .post(`${apiUri}/stornoRejectedBills`, data, {cancelToken: source.token})
      .then(response => response.data);
};

export const archiveRejectedBillsAndRecreateXml = (
    mandateId: number,
    tariffId: number,
    billXmlRequestIds: Array<Number>,
    source: CancelTokenSource)
    : Promise<String> => {

  const data = {
    mandateId,
    tariffId,
    billXmlRequestIds
  }

  return axios
      .post(`${apiUri}/archiveAndRecreateXmlBills`, data, {cancelToken: source.token})
      .then(response => response.data);
};

export const getRejectedBillsPdfUrl = (
    rejectedBillsPdfId: string,
    rejectedBillsPdfName: string) => {

  return `${baseUrl}/08d97fcc14e445e8b32a3830d640sb69/${rejectedBillsPdfId}/${rejectedBillsPdfName}`;
};

export const updateBillXmlResponsesFromMediData = (
    mandateId: number,
    source: CancelTokenSource): Promise<MediDataNotificationsDto> => {

  return axios
      .get(`${apiBillResponseUri}/update?mandateId=${mandateId}`,
          {cancelToken: source.token})
      .then(response => response.data);
};

export const getBillXmlRequestNotifications = (
    mandateId: number,
    fromDate: string,
    toDate: string,
    source: CancelTokenSource)
    : Promise<MediDataNotificationsDto> => {

  return axios
      .post(`${apiBillRequestUri}/medidata/notifications`,
          {},
          {params: {mandateId: mandateId, fromDate, toDate, limit: 5}, cancelToken: source.token})
      .then(response => response.data);
};

export const getBillXmlRequestNotificationsFromUrl = (
    mandateId: number,
    rel: string,
    url: string,
    source: CancelTokenSource)
    : Promise<MediDataNotificationsDto> => {

  const request = {
    mandateId: mandateId,
    url: url
  }

  return axios
      .post(`${apiBillRequestUri}/medidata/notifications/${rel}`, request, {cancelToken: source.token})
      .then(response => response.data);
};

export const putBillXmlRequestNotificationStatus = (
    mandateId: number,
    notificationId: number,
    source: CancelTokenSource)
    : Promise<MediDataNotificationsDto> => {

  return axios
      .put(`${apiBillRequestUri}/medidata/notifications/${notificationId}/status`,
          {},
          {params: {mandateId: mandateId}, cancelToken: source.token})
      .then(response => response.data);
};

export const migrateLegacyMediDataDownloadsMisanto = (
    source: CancelTokenSource)
    : Promise<string> => {

  return axios
      .post(`${apiBillResponseUri}/migrate-legacy-misanto-downloads`,
          {},
          {cancelToken: source.token})
      .then(response => response.data);
};

export const migrateLegacyMediDataDownloadsDiamisanto = (
    source: CancelTokenSource)
    : Promise<string> => {

  return axios
      .post(`${apiBillResponseUri}/migrate-legacy-diamisanto-downloads`,
          {},
          {cancelToken: source.token})
      .then(response => response.data);
};
